import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import type { Database } from "@/integrations/supabase/types";

export const quoteSchema = z.object({
  name: z.string().trim().min(1).max(200),
  email: z.string().trim().email().max(320),
  phone: z.string().trim().max(40).optional().or(z.literal("")),
  address: z.string().trim().max(500).optional().or(z.literal("")),
  service_type: z.string().trim().max(100).optional().or(z.literal("")),
  message: z.string().trim().max(5000).optional().or(z.literal("")),
  source_page: z.string().trim().max(200).regex(/^[a-zA-Z0-9\-\/]*$/).optional().or(z.literal("")),
});

export type QuoteInput = z.infer<typeof quoteSchema>;

const RATE_LIMIT_WINDOW_MINUTES = 5;
const RATE_LIMIT_MAX_SUBMISSIONS = 3;

export const submitQuote = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => quoteSchema.parse(data))
  .handler(async ({ data }) => {
    const SUPABASE_URL = process.env.SUPABASE_URL;
    const SUPABASE_PUBLISHABLE_KEY = process.env.SUPABASE_PUBLISHABLE_KEY;

    if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
      console.error("[submitQuote] Missing Supabase environment variables");
      throw new Error("Server configuration error.");
    }

    // Rate limiting check using admin client (read-only count query).
    // supabaseAdmin is intentionally used here only for reading; the actual
    // insert below uses an unauthenticated client so RLS policies apply.
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const windowStart = new Date(
      Date.now() - RATE_LIMIT_WINDOW_MINUTES * 60 * 1000
    ).toISOString();

    const { count, error: countError } = await supabaseAdmin
      .from("quote_requests")
      .select("*", { count: "exact", head: true })
      .eq("email", data.email)
      .gte("created_at", windowStart);

    if (countError) {
      console.error("[submitQuote] rate limit check error", countError);
      throw new Error("Could not submit your request. Please try again later.");
    }

    if ((count ?? 0) >= RATE_LIMIT_MAX_SUBMISSIONS) {
      throw new Error(
        "Too many submissions from this email. Please wait a few minutes."
      );
    }

    // Use unauthenticated client so RLS INSERT policy applies
    const { createClient } = await import("@supabase/supabase-js");
    const supabase = createClient<Database>(
      SUPABASE_URL,
      SUPABASE_PUBLISHABLE_KEY,
      {
        auth: {
          storage: undefined,
          persistSession: false,
          autoRefreshToken: false,
        },
      }
    );

    const { error } = await supabase.from("quote_requests").insert({
      name: data.name,
      email: data.email,
      phone: data.phone || null,
      address: data.address || null,
      service_type: data.service_type || null,
      message: data.message || null,
      source_page: data.source_page || null,
    });

    if (error) {
      console.error("[submitQuote]", error);
      throw new Error(
        "Could not submit your request. Please try again or call us."
      );
    }

    // Notify the business and confirm receipt with the customer.
    try {
      const { enqueueAppEmail } = await import("@/lib/email/enqueue.server");
      await enqueueAppEmail({
        templateName: "quote-notification",
        templateData: {
          name: data.name,
          email: data.email,
          phone: data.phone || undefined,
          address: data.address || undefined,
          service_type: data.service_type || undefined,
          message: data.message || undefined,
          source_page: data.source_page || undefined,
        },
      });
      await enqueueAppEmail({
        templateName: "quote-confirmation",
        recipientEmail: data.email,
        templateData: { name: data.name, service_type: data.service_type || undefined },
      });
    } catch (emailError) {
      console.error("[submitQuote] notification email failed", emailError);
    }

    return { ok: true as const };
  });
